import { difference, find, min, some } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { Snackbar } from 'src/components/snackbar';
import { COLLECTIONS } from 'src/constants';
import { USER_ACTIONS } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import {
  useGetAllLocationOwnerTags,
  useGetLocation,
  useModal,
  useUpdateLocation,
  useUpdateLocations,
  useViewChannels,
} from 'src/hooks';
import { validateTagName } from 'src/hooks/api/locationTags/helper';
import { useCreateOwnerLocationTag } from 'src/hooks/api/locationTags/useCreateOwnerLocationTags';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { useApiErrorNavigator } from 'src/hooks/navigation';
import {
  ILocation,
  ILocationTag,
  IViewLocationGroupList,
  LayoutTypes,
} from 'src/interfaces';
import { lang } from 'src/utils';
import {
  assignLocationTagToLocationEvent,
  ooCreateAndAssignNewLocationTag,
  OOLocationTagRemovedLocationEvent,
} from 'src/utils/google-tag-manager';

import { mergeConfigWithBannerAndMessage } from '../DetailLocationPage.helper';

export const useLocationEditPage = (locationCode = '') => {
  const { userRole } = useContext(AuthDataProvider);
  const navigate = useNavigate();
  const saveModal = useModal();
  const publishModal = useModal();
  const assignLocationGroupModal = useModal();
  const confirmLocationGroupModal = useModal();
  const previewLocationGroupsModal = useModal();
  const updateLocation = useUpdateLocation();
  const updateLocations = useUpdateLocations();
  const createOwnerLocationTag = useCreateOwnerLocationTag();

  const { data: location, isLoading: isLoadingLoaction } = useGetLocation(
    locationCode,
    userRole,
  );

  const { data: allOwnerTags, isLoading: isLoadingAllOwnerTags } =
    useGetAllLocationOwnerTags({
      enabled: userRole === AuthClassification.OWNER_OPERATOR,
    });

  const {
    data: channels = [],
    isLoading: isLoadingChannels,
    error,
  } = useViewChannels();
  useApiErrorNavigator(error);

  const channelsData = channels.map((item) => {
    return {
      ...item,
      locationGroup: location?.locationGroups?.find(
        (group) => group.channel_name === item.channel_name,
      ),
    };
  });

  const [ownerOperatorTags, setOwnerOperatorTags] = useState<ILocationTag[]>(
    [],
  );
  const [locationOwnerTagIds, setLocationOwnerTagIds] = useState<number[]>([]);

  const [selectedLocationGroups, setSelectedLocationGroups] = useState<
    IViewLocationGroupList[]
  >([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [locationTagChanged, setLocationTagChanged] = useState<boolean>(false);
  const [locationTagCount, setLocationTagCount] = useState<number>(0);
  const [isBannerEnabled, setIsBannerEnabled] = useState<boolean>(true);
  const [isMessagingEnabled, setIsMessagingEnabled] = useState<boolean>(true);
  const [isBannerChanged, setIsBannerChanged] = useState<boolean>(false);
  const [isMessagingChanged, setIsMessagingChanged] = useState<boolean>(false);
  const formMethods = useForm({
    values: {
      location: location,
    },
  });

  const assignedLocationOwnerTags =
    formMethods.getValues().location?.location_owner_tag_id;
  useEffect(() => {
    if (allOwnerTags) {
      setOwnerOperatorTags(allOwnerTags);
    } else if (location?.locationOwnerTags) {
      setOwnerOperatorTags(location.locationOwnerTags);
    }
  }, [allOwnerTags, location?.locationOwnerTags]);
  useEffect(() => {
    const codConfig = (location?.config as Record<string, any>)?.cod ?? {};
    const messagingEnabledValue = !(
      codConfig.codSetting?.topMessage?.hideRmhcMessage ?? false
    );

    const bannerEnabledValue =
      codConfig.confirmSetting?.header?.review?.banner?.active ?? true;

    setIsMessagingEnabled(messagingEnabledValue);
    setIsBannerEnabled(bannerEnabledValue);
  }, [location]);
  useEffect(() => {
    setLocationOwnerTagIds(assignedLocationOwnerTags ?? []);
  }, [assignedLocationOwnerTags]);

  useEffect(() => {
    if (location?.location_tag_id) {
      setLocationTagCount(location.location_tag_id.length);
    }
  }, [location]);

  useEffect(() => {
    saveModal.onCloseModal();
    publishModal.onCloseModal();
    assignLocationGroupModal.onCloseModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationCode]);

  const navigateToLocationGroupEdit = (id?: number) => {
    if (id !== undefined) {
      navigate(PATHS.LOCATION_GROUP_TIMINGS(String(id)));
    }
  };

  const addLocationTag = (tagId: string[]) => {
    setLocationTagChanged(true);
    formMethods.setValue('location.location_tag_id', tagId.map(Number));
    setLocationTagCount(
      Number(formMethods?.getValues()?.location?.location_tag_id.length),
    );
  };

  const addLocationOwnerTag = (tagId: string[]) => {
    if (tagId.length > locationOwnerTagIds.length) {
      const addedTag = difference(tagId.map(Number), locationOwnerTagIds)[0];
      assignLocationTagToLocationEvent(locationCode, addedTag);
    } else {
      const removedTag = difference(locationOwnerTagIds, tagId.map(Number))[0];
      formMethods.setValue('location.location_owner_tag_id', tagId.map(Number));
      OOLocationTagRemovedLocationEvent(location?.location_code, removedTag);
    }
    setLocationTagChanged(true);
    setLocationOwnerTagIds(tagId.map(Number));
  };

  const removeLocationOwnerTag = (tagId: number) => {
    const tags = locationOwnerTagIds || [];
    const updatedData = tags.filter((item) => Number(item) !== Number(tagId));
    setLocationTagChanged(true);
    setLocationOwnerTagIds(updatedData);
    formMethods.setValue('location.location_owner_tag_id', updatedData);
    OOLocationTagRemovedLocationEvent(location?.location_code, tagId);
  };

  const createOwnerTag = (newTag: string) => {
    const newId =
      (min(
        locationOwnerTagIds?.filter((item) => item < 0).map((item) => item),
      ) ?? 0) - 1;

    const tag = {
      id: newId,
      name: newTag,
    } as ILocationTag;

    const validation = validateTagName(tag.name);
    if (!!validation) {
      Snackbar.error(`${lang.canNotAddNewTag} ${validation}.`);
    } else {
      setOwnerOperatorTags((prevState) => [...prevState, tag]);
      setLocationOwnerTagIds((prevState) => [...prevState, tag.id]);
      setLocationTagChanged(true);
    }
  };

  const createNewOwnerOperatorTags = async (): Promise<number[]> => {
    const newTagIds = locationOwnerTagIds.filter((id) => id < 0);
    const savedOOTagIds: number[] = [];
    await Promise.all(
      newTagIds.map(async (id) => {
        const tag = find(ownerOperatorTags, (ooTag) => ooTag.id === id);
        try {
          if (tag === undefined) return true;

          const request = {
            name: tag.name.trim(),
          };

          const result = await createOwnerLocationTag.mutateAsync(request);

          if (result.id) {
            tag.id = result.id;

            ooCreateAndAssignNewLocationTag(locationCode, tag.name, result.id);
            savedOOTagIds.push(result.id);
          }
        } catch (error: any) {
          if (error?.response?.data?.error?.includes('Duplicate entry')) {
            Snackbar.error(
              `${lang.failedToCreateTag} ${tag?.name} ${lang.alreadyExists}`,
            );
          } else {
            Snackbar.error(`${lang.failedToCreateTag} ${tag?.name}.`);
          }
        }

        return 0;
      }),
    );
    setLocationOwnerTagIds((prevState) => [
      ...(prevState.filter((id) => id > 0) ?? []),
      ...savedOOTagIds,
    ]);

    return savedOOTagIds;
  };

  const onClickSave = async () => {
    if (locationTagChanged || isBannerChanged || isMessagingChanged) {
      if (locationOwnerTagIds?.length > 0) {
        const location_owner_tag_ids = locationOwnerTagIds.filter(
          (id) => id > 0,
        );
        if (some(locationOwnerTagIds, (id) => id < 0)) {
          const newOOTagIds = await createNewOwnerOperatorTags();
          formMethods.setValue('location.location_owner_tag_id', [
            ...location_owner_tag_ids,
            ...newOOTagIds,
          ]);
        } else {
          formMethods.setValue(
            'location.location_owner_tag_id',
            location_owner_tag_ids,
          );
        }
      }

      const payload = formMethods.getValues().location as ILocation;
      const existingConfig = (payload.config ?? {}) as Record<string, any>;

      const mergedConfig = mergeConfigWithBannerAndMessage(
        existingConfig,
        isBannerEnabled,
        isMessagingEnabled,
      );

      const narrative = narrativeGenerators[COLLECTIONS.LOCATIONS][
        USER_ACTIONS.UPDATE
      ](
        isBannerEnabled,
        isMessagingEnabled,
        isBannerChanged,
        isMessagingChanged,
        locationCode,
      );

      await updateLocation.mutateAsync({
        config: mergedConfig,
        location_code: payload.location_code,
        location_tag_id: payload.location_tag_id,
        location_owner_tag_id: payload.location_owner_tag_id,
        narrative,
      });

      setLocationTagChanged(false);
      setIsBannerChanged(false);
      setIsMessagingChanged(false);
      saveModal.onCloseModal();
      publishModal.onOpenModal();
    }
  };

  const publicationCloseModal = () => {
    publishModal.onCloseModal();
  };

  const filteredChannelsData = channelsData.filter((item) =>
    (location?.hq?.channels ?? []).includes(item.channel_name),
  );

  const isContainsOutdoorType = filteredChannelsData.some(
    (item) => item.layoutType?.name === LayoutTypes.OUTDOOR,
  );

  return {
    formMethods,
    isLoadingLoaction,
    channelsData: filteredChannelsData,
    isLoadingChannels,
    locationOwnerTagIds,
    ownerOperatorTags,
    isLoadingAllOwnerTags,
    saveModal,
    publishModal,
    assignLocationGroupModal,
    confirmLocationGroupModal,
    previewLocationGroupsModal,
    updateLocations,
    locationTagChanged,
    selectedLocationGroups,
    setSelectedLocationGroups,
    isSaving,
    setIsSaving,
    addLocationTag,
    addLocationOwnerTag,
    removeLocationOwnerTag,
    createOwnerTag,
    onClickSave,
    navigateToLocationGroupEdit,
    publicationCloseModal,
    locationTagCount,
    location,
    isBannerEnabled,
    isMessagingEnabled,
    setIsBannerEnabled,
    setIsMessagingEnabled,
    setIsBannerChanged,
    setIsMessagingChanged,
    isBannerChanged,
    isMessagingChanged,
    isContainsOutdoorType,
  };
};
